import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { checkEmail, type EmailCheckSurface } from '@/lib/email-check-api';
import {
  EMAIL_CHECK_DEBOUNCE_MS,
  initial,
  reduce,
  type EmailCheckEvent,
  type EmailCheckTone,
} from '@/cockpit/email-check';

// The row an SDR types a heard address into. Owns the timer, the abort and the DOM;
// the verdicts are decided in ./email-check.

// Well outside the edge's own bound, so reaching it means nothing is coming back.
const REQUEST_CEILING_MS = 20_000;

const TONE: Record<EmailCheckTone, string> = {
  idle: 'text-muted-foreground',
  checking: 'text-muted-foreground',
  valid: 'text-emerald-600',
  unconfirmed: 'text-amber-600',
  invalid: 'text-destructive',
  unchecked: 'text-amber-600',
  error: 'text-destructive',
};

function Icon({ tone }: { tone: EmailCheckTone }) {
  if (tone === 'valid') return <Check className="size-4 shrink-0" aria-hidden />;
  if (tone === 'invalid' || tone === 'error') return <X className="size-4 shrink-0" aria-hidden />;
  if (tone === 'unconfirmed' || tone === 'unchecked')
    return <TriangleAlert className="size-4 shrink-0" aria-hidden />;
  return null;
}

export function EmailCheck({
  enabled,
  callKey,
  surface,
}: {
  // False leaves the row disabled rather than offering a check that cannot answer.
  enabled: boolean;
  // A new value is a new person on the line, and a full reset.
  callKey: string;
  surface: EmailCheckSurface;
}) {
  const [state, setState] = useState(initial);
  // Authoritative; `setState` only renders. Reducing inside an updater would let
  // StrictMode run it twice and spend two checks.
  const current = useRef(state);
  const inFlight = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [copied, setCopied] = useState(false);

  const dispatch = useCallback(
    (event: EmailCheckEvent) => {
      const { state: next, effect } = reduce(current.current, event);
      current.current = next;
      setState(next);

      if (effect?.t === 'cancel' || effect?.t === 'request') {
        inFlight.current?.abort();
        inFlight.current = null;
      }
      if (effect?.t === 'request') {
        const controller = new AbortController();
        inFlight.current = controller;
        setCopied(false);
        const { email, seq } = effect;
        // Answers as well as aborts: a stall before the fetch (minting a token) is
        // out of the abort's reach, and only a failure offers the SDR a retry.
        const bail = setTimeout(() => {
          controller.abort();
          dispatch({ t: 'response', seq, res: { ok: false, reason: 'timeout' }, at: new Date() });
        }, REQUEST_CEILING_MS);
        void checkEmail(email, surface, controller.signal).then((res) => {
          clearTimeout(bail);
          dispatch({ t: 'response', seq, res, at: new Date() });
        });
      }
    },
    [surface],
  );

  // A card replaced mid-typing must not keep the last lead's verdict.
  useEffect(() => {
    dispatch({ t: 'call_changed' });
    setCopied(false);
  }, [callKey, dispatch]);

  useEffect(() => () => inFlight.current?.abort(), []);

  const { input, view } = state;

  useEffect(() => {
    if (!enabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => dispatch({ t: 'debounce_elapsed' }), EMAIL_CHECK_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [input, enabled, dispatch]);

  const copy = useCallback(() => {
    if (!view.copy) return;
    // A refused clipboard must not throw; the ref stays on screen to read out.
    void navigator.clipboard
      ?.writeText(view.copy)
      .then(() => setCopied(true))
      .catch(() => undefined);
  }, [view.copy]);

  return (
    <div className="border-t pt-3" data-testid="email-check">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="email-check-input" className="text-muted-foreground text-sm">
          Check email
        </label>
        <input
          id="email-check-input"
          type="email"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          // Never autofocus: focus here suppresses the call's keyboard shortcuts.
          disabled={!enabled}
          value={input}
          onChange={(e) => dispatch({ t: 'typed', value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              dispatch({ t: 'submit' });
            }
          }}
          placeholder={enabled ? 'name@company.com' : 'Email checking isn’t set up yet'}
          // Session replay records this app unmasked.
          data-dd-privacy="mask"
          className="border-input bg-background min-w-0 flex-1 rounded-md border px-2 py-1 text-sm disabled:opacity-60"
          data-testid="email-check-input"
        />
        {view.badge && (
          <span
            className={cn('flex items-center gap-1 text-sm font-medium', TONE[view.tone])}
            data-dd-privacy="mask"
            data-testid="email-check-badge"
            role="status"
          >
            <Icon tone={view.tone} />
            {view.badge}
          </span>
        )}
        {view.tone === 'checking' && (
          <span className="text-muted-foreground text-sm" role="status">
            Checking…
          </span>
        )}
      </div>

      {(view.note || view.nudge) && (
        <div className="mt-1 space-y-0.5 text-sm" data-dd-privacy="mask">
          {view.note && <p className={TONE[view.tone]}>{view.note}</p>}
          {view.nudge && (
            <p className="text-amber-600" data-testid="email-check-nudge">
              {view.nudge}
            </p>
          )}
        </div>
      )}

      {(view.retry || view.copy) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {view.retry && (
            <button
              type="button"
              onClick={() => dispatch({ t: 'retry' })}
              className="border-border hover:bg-accent rounded-md border px-2 py-1 text-xs font-medium"
              data-testid="email-check-retry"
            >
              Retry
            </button>
          )}
          {view.copy && (
            <>
              <button
                type="button"
                onClick={copy}
                className="border-border hover:bg-accent flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium"
                data-testid="email-check-copy"
              >
                <Copy className="size-3" aria-hidden />
                {copied ? 'Copied' : 'Copy'}
              </button>
              {view.ref && (
                <span className="text-muted-foreground font-mono text-xs">ref {view.ref}</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
